import { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ArticleReference,
  Chat,
  AiResearchType,
  isArticleChunkReference
} from "models/AiResearch";
import { useHistoryList } from "./useHistoryList";
import { useQuery } from "./useQuery";
import { useHistoryChat } from "./useHistoryChat";
import { useDownload } from "./useDownload";
import { useReference } from "./useReference";
import { useHistoryUpdate } from "./useHistoryUpdate";
import { useChatDelete } from "./useChatDelete";
import { useToast } from "hooks/useToast";

export const useAiResearch = () => {
  const [type, setType] = useState<AiResearchType>("newChat");
  const [result, setResult] = useState<Chat>({ title: "", history: [] });

  const { pathname, search, state } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const globalSearchState = (state as {
    aiResearchRequestId?: number;
  } | null) ?? {
    aiResearchRequestId: undefined
  };
  const aiResearchRequestId = globalSearchState.aiResearchRequestId ?? 0;
  const aiResearchTriggerKey = aiResearchRequestId;
  const lastPathRef = useRef(pathname);
  const params = new URLSearchParams(search);
  const talkId = params.get("talkId");

  // 履歴一覧取得
  const { history, fetchHistoryList, replaceHistory } = useHistoryList();
  // チャット履歴更新用
  const { updateHistory } = useHistoryUpdate({ replaceHistory, setResult });
  // チャット履歴削除
  const { deleteChat } = useChatDelete({ replaceHistory });
  // 質問クエリ送信
  const { sseStatus, sendQuestion, close } = useQuery({
    result,
    setResult,
    fetchHistoryList: fetchHistoryList
  });
  // 過去のチャット表示用
  const { fetchHistoryChat } = useHistoryChat({
    setResult
  });
  // ダウンロード処理用
  const { onDownload } = useDownload();
  // 引用情報を取得
  const {
    reference,
    isLoading: isReferenceLoading,
    fetchReference,
    clearReference
  } = useReference({
    result
  });

  // SSEへ接続中
  const isLoading = sseStatus === "connecting";
  // 回答生成中
  const isOpen = sseStatus === "open";

  // 初回表示時にtalkIdがあれば履歴を表示
  useEffect(() => {
    if (talkId) {
      onShowHistory(talkId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 一括検索のAIリサーチボタン押下時の処理
  useEffect(() => {
    // AIチャット画面(/ai-research)から一括検索フォーム横のAIリサーチボタンを押された時、新規チャット画面へ遷移
    if (aiResearchTriggerKey) {
      setResult({ title: "", history: [] });
      setType("newChat");
      close();
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiResearchTriggerKey]);

  // AIリサーチからページを離れた時にSSEを閉じる
  useEffect(() => {
    if (lastPathRef.current === pathname) return;
    const wasAiResearch = lastPathRef.current.startsWith("/ai-research");
    const isAiResearch = pathname.startsWith("/ai-research");

    if (wasAiResearch && !isAiResearch) {
      close();
    }
    lastPathRef.current = pathname;
  }, [close, pathname]);

  // チャット結果をクリア
  const clearResult = useCallback(() => {
    setResult({ title: "", history: [] });
    clearReference();
  }, [clearReference]);

  // URLのtalkIdを削除
  const clearTalkIdParam = useCallback(() => {
    const nextParams = new URLSearchParams(search);
    nextParams.delete("talkId");

    const nextSearch = nextParams.toString();
    const nextPath = `${pathname}${nextSearch ? `?${nextSearch}` : ""}`;
    const nextState = {
      ...(typeof state === "object" && state !== null ? state : {}),
      reserveToast: true
    };

    navigate(nextPath, { state: nextState });
  }, [navigate, pathname, search, state]);

  // 新規チャット作成時の処理
  const onClickNewChat = () => {
    setType("newChat");
    clearResult();
    close();
  };

  // 過去のチャット表示処理
  const onShowHistory = useCallback(
    (talkId: string) => {
      setType("history");
      clearResult();
      close();
      fetchHistoryChat({ talkId });
    },
    [clearResult, close, fetchHistoryChat]
  );

  // 引用情報取得処理
  const onShowReference = ({
    questionId,
    citationNo
  }: {
    questionId: string;
    citationNo: number;
  }) => {
    if (sseStatus !== "closed" && sseStatus !== "idle") return;

    fetchReference({ questionId, citationNo });
  };

  // 回答コピー処理
  const onCopy = ({ chatIndex }: { chatIndex: number }) => {
    const reference = result.history[chatIndex].reference
      ?.filter((item): item is ArticleReference =>
        isArticleChunkReference(item)
      )
      .sort((a, b) => a.citationNumber - b.citationNumber)
      .map(ref => {
        return `- [${ref.citationNumber}] ${ref.data.title}`;
      })
      .join("\n");

    navigator.clipboard.writeText(
      `${result.history[chatIndex].answer}${
        reference ? `\n\nソース\n${reference}` : ""
      }`
    );
  };

  // 停止ボタン押下時の処理
  const onClose = () => {
    close();
    setResult(prev => ({
      ...prev,
      history: prev.history.map((item, index) => {
        if (prev.history.length - 1 === index) {
          return {
            ...item,
            isError: true,
            answer: "回答を停止しました"
          };
        }

        return { ...item };
      })
    }));
  };

  // チャットタイトルを変更後、履歴を取得する
  const onChatTitleEdit = async ({
    newTitle,
    talkId
  }: {
    newTitle: string;
    talkId: string;
  }) => {
    await updateHistory({
      talkId,
      title: newTitle
    });
    toast.setMessages("チャットの名前を変更しました");
  };
  // 画面サイズ変更時の処理
  const onRetry = (text: string) => {
    sendQuestion(text, true);
  };

  // チャット履歴を削除する
  const onChatDelete = async ({ talkId }: { talkId: string }) => {
    await deleteChat({
      talkId
    });
    if (result.talkId === talkId) {
      setType("newChat");
      clearTalkIdParam();
      clearResult();
    }
    toast.setMessages("チャット履歴を削除しました");
  };

  return {
    result,
    type,
    isLoading,
    isReferenceLoading,
    isOpen,
    history,
    reference,
    setType,
    onShowHistory,
    onShowReference,
    clearReference,
    onSendQuestion: sendQuestion,
    onDownload,
    onClickNewChat,
    onClose,
    onCopy,
    onChatTitleEdit,
    onChatDelete,
    onRetry
  };
};
